import { useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { LoaderCircleIcon } from 'lucide-react';
import { useCurrentWorkspaceId } from '@/entities/workspace/model/workspace.store';
import { getDownloadable, isDownloadableType, type Downloadable } from './Downloadables.registry';
import { DOWNLOAD_ROOT_ID, waitForStableHeight } from './Download.utils';
import './Download.styles.css';
import { useTranslation } from 'react-i18next';

/**
 * Chrome-less `/download/:documentType/:id` route printed to PDF by a headless
 * browser. The document and its fetch come from the `DOWNLOADABLES` registry.
 * Signals `window.__PDF_READY__` once content + fonts/images have settled.
 */
declare global {
  interface Window {
    __PDF_READY__?: boolean;
  }
}

export function Download() {
  const { documentType } = useParams<{ documentType: string }>();
  const downloadable = isDownloadableType(documentType) ? getDownloadable(documentType) : undefined;

  if (!downloadable) return <DownloadNotFound />;

  // Keyed by type so switching entries remounts: each `useRender` may call different hooks.
  return <DownloadDocument key={documentType} downloadable={downloadable} />;
}

function DownloadDocument({ downloadable }: { downloadable: Downloadable<unknown> }) {
  const { t } = useTranslation('springboard');

  const { documentType, id } = useParams<{ documentType: string; id: string }>();
  const [searchParams] = useSearchParams();
  // Store only: `initializeWorkspace` seeds it from `?workspaceId=` at boot after
  // validating membership, so the raw (cross-workspace) param never reaches the fetch.
  const workspaceId = useCurrentWorkspaceId();

  // Per-document hints forwarded to `fetch` and the renderer; ignored otherwise.
  const section = searchParams.get('section') ?? undefined;
  const itemIndexParam = searchParams.get('itemIndex');
  const itemIndex = itemIndexParam != null ? Number(itemIndexParam) : undefined;
  const render = downloadable.useRender();

  const { fetch } = downloadable;
  const { data, isLoading, isError } = useQuery({
    queryKey: ['download', documentType, workspaceId, id, section, itemIndex, fetch],
    // Inline the hints (not a captured `opts` object) so exhaustive-deps sees
    // the same `section`/`itemIndex` that are in the key.
    queryFn: () => fetch(workspaceId!, id!, { section, itemIndex }),
    enabled: !!workspaceId && !!id,
  });

  // Flip __PDF_READY__ once content + fonts/images settle; cleared on
  // unmount/re-fetch so a capture never fires against half-rendered content.
  useEffect(() => {
    window.__PDF_READY__ = false;
    if (!data) return;

    // `active` guards against setting the flag after unmount/re-fetch.
    const active = { current: true };
    void (async () => {
      await document.fonts.ready;
      await waitForStableHeight();
      if (active.current) window.__PDF_READY__ = true;
    })();

    return () => {
      active.current = false;
      window.__PDF_READY__ = false;
    };
  }, [data]);

  if (isLoading) {
    return (
      <div className="flex h-dvh items-center justify-center bg-white">
        <LoaderCircleIcon className="size-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="flex h-dvh items-center justify-center bg-white">
        <p className="text-sm text-muted-foreground">{t('download.documentNotFound')}</p>
      </div>
    );
  }

  return (
    <div className="min-h-dvh bg-white">
      <div id={DOWNLOAD_ROOT_ID} className="download-root">
        {render(data, { section, itemIndex })}
      </div>
    </div>
  );
}

function DownloadNotFound() {
  const { t } = useTranslation('springboard');
  return (
    <div className="flex h-dvh items-center justify-center bg-white">
      <p className="text-sm text-muted-foreground">{t('download.notFound')}</p>
    </div>
  );
}
